//
// SchemaPinboardStore
//
// The SchemaPinboardStore handles interaction with the pinboard area for pinning
// definitions, classes, and fields.
// Should work with treeview and search results.
//
// SIDE-EFFECT: when a user selects an item, we update the worksheet with that
//              same item

import { action, computed, makeObservable, observable } from 'mobx'
import { getSchemaitemStore, resolveBaseGid } from '../data/utils'

export class SchemaPinboardStore {
    __init = false
    _key = null
    _rootstore = null
    _pinnedGids = null

    constructor(key, rootstore) {
        makeObservable(this, {
            __init: observable,
            _key: observable,
            _rootstore: observable,
            _pinnedGids: observable,
            pinnedItems: computed,
            loadOnce: action.bound,
            fetch: action.bound,
            _fetch: action.bound,
            add: action.bound,
            remove: action.bound,
            clear: action.bound,
            selectItem: action.bound,
        })

        this.MAX_ITEMS = 5

        this._key = key
        this._rootstore = rootstore
        this._pinnedGids = []
    }

    // We persist only base gids in _pinnedGids. pinnedItems resolves each base gid
    // to its working copy when one exists, otherwise to the base item itself, so a
    // modified pinned item is shown (and reacts) as its working copy.
    get pinnedItems() {
        return this._pinnedGids
            .map(gid => this.resolvePinnedItemByGid(gid))
            .filter(item => item !== null)
    }

    loadOnce = () => {
        if (this.__init) return
        const pinnedGids = this._rootstore.view.loadPerProject(
            this._key + '/pinnedGids',
            []
        )
        this.fetch(pinnedGids)
        this._rootstore.api.register_syncdataitem_callback(this.syncdataitem_callback)
        this.__init = true
    }

    // fetch owns persistence; _fetch owns resolving/pruning into _pinnedGids.
    fetch(pinnedGids) {
        this._rootstore.view.savePerProject(this._key + '/pinnedGids', pinnedGids)
        this._fetch(pinnedGids)
    }

    findOriginal(gid) {
        const store = getSchemaitemStore(gid, this._rootstore.data)
        if (!store) return gid
        const item = store.get(gid)
        return item ? resolveBaseGid(item) : gid
    }

    resolvePinnedItemByGid(itemgid) {
        const store = getSchemaitemStore(itemgid, this._rootstore.data)
        if (!store) return null
        const base = store.get(itemgid)
        if (!base) return null
        // We store the base gid; show the working copy if one exists, otherwise the base.
        let resolved = base
        store.forEach(item => {
            if (item.is_working_copy && item.original === base.gid) {
                resolved = item
            }
        })
        return resolved
    }

    _fetch(pinnedGids) {
        // _pinnedGids only ever holds base gids that still resolve to a schema item.
        // foundGids is an order-preserving subset of pinnedGids, so a length change
        // is enough to know we pruned something and need to re-persist.
        const foundGids = pinnedGids.filter(
            itemgid => this.resolvePinnedItemByGid(itemgid) !== null
        )
        this._pinnedGids.replace(foundGids)
        if (foundGids.length !== pinnedGids.length) {
            this._rootstore.view.savePerProject(this._key + '/pinnedGids', foundGids)
        }
    }

    exists(gid) {
        return this._pinnedGids.includes(this.findOriginal(gid))
    }

    canAdd() {
        return this._pinnedGids.length < this.MAX_ITEMS
    }

    add(gid) {
        if (!gid) return
        const baseGid = this.findOriginal(gid)
        if (this.exists(baseGid)) return // no duplicates
        if (!this.canAdd()) return
        this._pinnedGids.push(baseGid)
        this.fetch(this._pinnedGids)
    }

    remove(gid) {
        this._pinnedGids.remove(this.findOriginal(gid))
        this.fetch(this._pinnedGids)
    }

    clear() {
        this._pinnedGids.clear()
        this.fetch(this._pinnedGids)
    }

    selectItem(item) {
        this._rootstore.view.schemaworksheet.setItem(item)
    }

    syncdataitem_callback = (syncdataitem, data_before, data_after) => {
        if (!['definitions', 'classes', 'fields'].includes(syncdataitem['data_type'])) {
            return
        }
        // We persist base gids and resolve working copies reactively in pinnedItems,
        // so INSERT/UPDATE of a working copy needs no bookkeeping here. The only list
        // mutation is pruning a pinned base item when it is deleted; deleting a working
        // copy leaves its base gid pinned and pinnedItems falls back to the base.
        if (syncdataitem['action'] !== 'DELETE') return
        if (!data_before || !this._pinnedGids.includes(data_before.gid)) return
        this.fetch(this._pinnedGids.filter(gid => gid !== data_before.gid))
    }
}
